import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSportsData } from '../hooks/useSportsData';
import { sportStyles } from '../utils/colorUtils';

// Icons for subcategories (no extra fetch needed)
const SUB_ICONS = {
  // Dance
  hiphop: '🕺', bollywood: '💃', salsa: '💃', bachata: '💃', kizomba: '🌹', jive: '⚡',
  tango: '🌹', waltz: '🌀', zumba: '🎉', contemporary: '🩰',
  // Gym
  legs: '🦵', shoulders: '💪', back: '🔙', chest: '🏆', core: '🎯',
  // Yoga
  hatha: '🧘', vinyasa: '🌊', ashtanga: '🔥', yin: '🌙',
  // Martial Arts
  karate: '🥋', taekwondo: '🦵', judo: '🤼', muaythai: '🥊', bjj: '🤸', kickboxing: '👊',
  // Swimming
  freestyle: '🏊', breaststroke: '🤽', backstroke: '🌊', butterfly: '🦋',
  // Cycling
  roadcycling: '🚴', mountainbike: '🏔️', spincycling: '🎯',
  // Skating
  iceskating: '⛸️', inlineskating: '🛼', figureskating: '🌟', speedskating: '💨',
};

export default function SportsOnboarding({ favorites, onToggle, onComplete, onSkip }) {
  const { t } = useTranslation();
  const { index, loading } = useSportsData();
  const categories = index?.categories ?? [];

  // Which parent categories (with subcategories) are selected
  const selectedParents = categories
    .filter(c => c.subcategories && favorites.includes(c.id));

  return (
    <div className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'radial-gradient(ellipse at 20% 10%, #d1fae5 0%, #f8fafc 50%, #ede9fe 100%)' }}>
      <div className="w-full max-w-lg">

        {/* Header */}
        <div className="mb-8 text-center">
          <p className="text-4xl mb-3">🏅</p>
          <h1 className="text-2xl font-black text-slate-900">What sports do you love?</h1>
          <p className="mt-2 text-sm text-slate-500">
            Tap to pick your favorites. Walking is already added for you.
          </p>
        </div>

        {/* Sports grid */}
        {loading ? (
          <div className="grid grid-cols-4 gap-3 mb-6">
            {[...Array(8)].map((_, i) => <div key={i} className="h-20 animate-pulse rounded-2xl bg-slate-100" />)}
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-3 mb-4">
            {categories.map(cat => {
              const active = favorites.includes(cat.id);
              const st = sportStyles(cat.color);
              return (
                <button key={cat.id} onClick={() => onToggle(cat.id)}
                  className="relative rounded-2xl border-2 p-3 text-center transition-all duration-150 hover:-translate-y-0.5 hover:shadow-md"
                  style={active ? st.cardSelected : st.cardDefault}>
                  <span className="block text-2xl leading-none mb-1">{cat.icon}</span>
                  <span className="block text-[10px] font-bold leading-tight"
                    style={{ color: active ? '#fff' : cat.color }}>
                    {t(`sports.names.${cat.id}`, cat.label)}
                  </span>
                  {active && (
                    <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-white shadow text-[8px]">✓</span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Subcategory chips — shown for each selected parent */}
        {selectedParents.map(parent => (
          <div key={parent.id} className="mb-4 rounded-2xl border border-slate-200 bg-white p-4">
            <p className="mb-2 text-xs font-black text-slate-500 uppercase tracking-widest">
              {t(`sports.names.${parent.id}`, parent.label)} — pick styles
            </p>
            <div className="flex flex-wrap gap-2">
              {(parent.subcategories || []).map(subId => {
                const active = favorites.includes(subId);
                const st = sportStyles(parent.color);
                return (
                  <button key={subId} onClick={() => onToggle(subId)}
                    className="flex items-center gap-1.5 rounded-xl border-2 px-3 py-1.5 text-xs font-bold transition-all duration-150"
                    style={active ? st.cardSelected : st.cardDefault}>
                    <span>{SUB_ICONS[subId] ?? '🏅'}</span>
                    <span style={{ color: active ? '#fff' : parent.color }}>
                      {t(`sports.names.${subId}`, subId)}
                    </span>
                    {active && <span style={{ color: active ? 'rgba(255,255,255,0.8)' : parent.color }}>✓</span>}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        {/* Actions */}
        <div className="mt-6 space-y-3">
          <button onClick={onComplete}
            disabled={favorites.length === 0}
            className="w-full rounded-2xl bg-emerald-500 py-3.5 text-sm font-black text-white shadow-lg transition hover:bg-emerald-600 disabled:opacity-40">
            Get Started →
          </button>
          <button onClick={onSkip}
            className="w-full py-2 text-sm text-slate-400 transition hover:text-slate-600">
            Skip for now
          </button>
        </div>

      </div>
    </div>
  );
}
